import React, { useEffect, useRef, useState } from 'react'
import { kuralData } from "../data/kuralData";
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import { Link, useNavigate } from 'react-router-dom';
import Lottie from "lottie-react";
import loading from "../lottie/loading.json";
import {
  trackDashboardView,
  trackKuralWordSubmit,
  trackKuralComplete,
  trackKuralWordEnter,
  trackKuralAnswerCorrect,
  trackKuralAnswerWrong,
  trackTypeKuralTap,
  trackVideoPromptShown,
  trackHomeTap,
  trackScanBiscuitCtaTap,
  trackBiscuitScanStart,
  trackBiscuitScanSuccess,
  trackBiscuitScanFailure,
  trackKuralUnlocked,
  trackCashbackClaimTap,
} from '../utils/ga4Events';
import MenuPopup from '../components/MenuPopup';
import TnCPopup from "../components/TnCPopup";
import {
  getJourneyFlowStage,
  JOURNEY_FLOW_STAGES,
  resolveSecondLoginFlow,
  setJourneyFlowStage,
  getPostBiscuitScanTarget,
} from "../utils/journeyFlow";
import { redirectToRoute } from "../utils/routeRedirect";

const trackInteraction = async (action, value = null) => {
  try {
    await api.post(
      "/interaction/track",
      { action, value },
      { withCredentials: true }
    );
  } catch (error) {
    console.error("Interaction tracking failed:", error);
  }
};

function Dashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showCardClickPopup, setShowCardClickPopup] = useState(0);
  const [welcomeBackPopup, setWelcomeBackPopup] = useState(false);
  const [showTncPopup, setShowTncPopup] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [currentKural, setCurrentKural] = useState(0);
  const [level2UnlockedPopup, setLevel2UnlockedPopup] = useState(0);
  const [showMeaningPopup, setShowMeaningPopup] = useState({ isVisible: false, kuralObj: null });
  const [showCompletePopUp, setShowCompletePopUp] = useState(false);
  const [showOopsPopUp, setShowOopsPopUp] = useState(false);
  const [oopsMessage, setOopsMessage] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [inputs, setInputs] = useState({});
  const [userData, setUserData] = useState(null);
  const language = localStorage.getItem("appLanguage") || "en";
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [selectedVideoKural, setSelectedVideoKural] = useState(null);
  const [activeKurals, setActiveKurals] = useState([]);
  const [completedKurals, setCompletedKurals] = useState([]);
  const [typeKuralMode, setTypeKuralMode] = useState(false);
  const [recordVideoPopup, setRecordVideoPopup] = useState(false);

  // ── Tutorial video popup state ──
  const [showTutorialVideo, setShowTutorialVideo] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(1);

  // ── Native camera biscuit-scan state (no in-browser video feed) ──
  // scanPopupType: null | 'loading' | 'success' | 'success-without-cashback' | 'try_again' | 'error' | 'oops_simple'
  const [scanPopupType, setScanPopupType] = useState(null);
  const [scanPopupData, setScanPopupData] = useState(null);

  const fileInputRef = useRef(null);
  const scanStartTimeRef = useRef(null);
  const biscuitScanFailCountRef = useRef(0);

  const submittingCellRef = useRef(null);
  const lastCommitDedupeRef = useRef({ id: "", t: 0 });
  const wrongAttemptCountsRef = useRef({});

  const kural = kuralData[currentKural];
  const inputRefs = useRef([]);

  const howToPlayRef = useRef(null);
  const completePopupRef = useRef(null);
  const menuPopupRef = useRef(null);
  const oopsPopupRef = useRef(null);

  // ── Level → S3 video URL map ──
  const tutorialVideoUrls = {
    1: "https://thirukkural-images.s3.ap-south-1.amazonaws.com/level1.mp4",
    2: "https://thirukkural-images.s3.ap-south-1.amazonaws.com/level2.mp4",
  };

  const closePopup = (ref, setter) => {
    if (ref.current) {
      ref.current.classList.add('closing');
      setTimeout(() => setter(false), 250);
    } else {
      setter(false);
    }
  };

  // ── Fetch user data on mount ──
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get("/user/userData", { withCredentials: true });
        setUserData(response.data.data);
        const d = response.data.data;
        trackDashboardView({
          kurals_unlocked: (d?.kuralProgress?.length || 0),
          kurals_completed: 0,
          total_cashback_earned: d?.totalCashback || 0,
        });
      } catch (error) {
        console.log(error);
      }
    };
    fetchUserData();
  }, []);

  // ── Compute active/completed kurals from userData ──
  useEffect(() => {
    if (!userData) return;

    const progress = userData.kuralProgress || [];
    const map = {};
    progress.forEach((item) => {
      if (!map[item.kuralNumber]) map[item.kuralNumber] = [];
      map[item.kuralNumber].push(item);
    });

    const active = [];
    const completed = [];

    kuralData.forEach((k) => {
      const items = map[k.id] || [];
      if (items.length === 0) return;

      const filledPositions = items.map((p) => p.position);
      const isComplete = k.blanks.every((idx) => filledPositions.includes(idx));

      if (isComplete) {
        completed.push(k.id);
      } else {
        active.push(k.id);
      }
    });
    setActiveKurals(active);
    setCompletedKurals(completed);

    const totalKurals = kuralData.length;
    const welcomeAlreadyShown = localStorage.getItem('britannia_welcome_shown') === 'true';
    if (progress.length > 0 && completed.length < totalKurals && !welcomeAlreadyShown) {
      setWelcomeBackPopup(true);
      try {
        localStorage.setItem('britannia_welcome_shown', 'true');
      } catch (e) {
        // ignore storage errors
      }
    }

    if (progress.length === 0) {
      setShowPopup(true);
    } else {
      setShowPopup(false);
    }
  }, [userData]);

  // ── Sync inputs from userData progress ──
  useEffect(() => {
    if (!userData) return;
    const progress = userData.kuralProgress || [];
    setInputs((prev) => {
      const next = { ...prev };
      progress.forEach((item) => {
        if (!next[item.kuralNumber]) next[item.kuralNumber] = {};
        const kuralObj = kuralData.find((k) => k.id === item.kuralNumber);
        const tamilWord = kuralObj?.tamil?.[item.position] || (item.matchedWord || '');
        next[item.kuralNumber][item.position] = tamilWord;
      });
      return next;
    });
  }, [userData]);

  // ── Show meaning popup for kural completed via biscuit scan ──
  useEffect(() => {
    if (!userData) return;

    const pendingId = localStorage.getItem("pendingKuralComplete");
    if (!pendingId) return;

    const kuralId = Number(pendingId);
    const kuralObj = kuralData.find((k) => k.id === kuralId);

    if (!kuralObj) {
      localStorage.removeItem("pendingKuralComplete");
      return;
    }

    const filledPositions = (userData.kuralProgress || [])
      .filter((p) => p.kuralNumber === kuralId)
      .map((p) => p.position);

    const isComplete = kuralObj.blanks.every((idx) =>
      filledPositions.includes(idx)
    );

    localStorage.removeItem("pendingKuralComplete");

    if (isComplete) {
      trackKuralComplete({
        kural_id: kuralId,
        language: localStorage.getItem('appLanguage') || 'en',
        time_to_complete_sec: null,
      });
      setTimeout(() => {
        setShowMeaningPopup({ isVisible: true, kuralObj });
      }, 600);
    }
  }, [userData]);

  const getWordFromProgress = (kuralId, index) => {
    if (!userData?.kuralProgress) return "";
    const found = userData.kuralProgress.find(
      (p) => p.kuralNumber === kuralId && p.position === index
    );
    return found?.matchedWord || "";
  };

  // ── "Scan Biscuit" opens the instructions popup first ──
  const goToBiscuitScan = () => {
    if (!userData) {
      window.location.href = "/biscuit-scan";
      return;
    }
    setShowPopup(false);
    setShowCardClickPopup(0);
    setRecordVideoPopup(true);
  };

  const submitKuralWord = async (kuralId, index, value) => {
    try {
      const submitResponse = await api.post(
        "/user/kural/submit",
        { kuralNumber: kuralId, position: index, word: value.trim() },
        { withCredentials: true }
      );

      const response = await api.get("/user/userData", { withCredentials: true });
      const latestUser = response.data.data;
      setUserData(latestUser);

      return { submitResult: submitResponse.data, userData: latestUser };
    } catch (error) {
      console.log(error);
      return {
        submitResult: error?.response?.data || null,
        userData: null,
      };
    }
  };

  const isBlankWordCorrect = (kuralObj, index, rawValue) => {
    const v = (rawValue || "").trim();

    if (!v) return false;

    const typedNorm = v.normalize("NFC");

    const tamilExpected = (kuralObj.tamil[index] || "").trim().normalize("NFC");
    if (typedNorm === tamilExpected) return true;

    const englishObj = kuralObj.english[index];

    if (typeof englishObj === "string") {
      return v.toLowerCase() === englishObj.trim().toLowerCase();
    }

    const expectedWord = (englishObj?.word || "").trim().toLowerCase();
    const variants = (englishObj?.variants || []).map((variant) => variant.trim().toLowerCase());
    const typedLower = v.trim().toLowerCase();

    return typedLower === expectedWord || variants.includes(typedLower);
  };

  const maybeCompletePopupAfterSubmit = (kuralId, latestUser) => {
    const kuralObj = kuralData.find((k) => k.id === kuralId);
    if (!kuralObj || !latestUser) return;
    const filledPositions = (latestUser.kuralProgress || [])
      .filter((p) => p.kuralNumber === kuralId)
      .map((p) => p.position);
    const isNowComplete = kuralObj.blanks.every((idx) => filledPositions.includes(idx));
    if (isNowComplete) {
      trackKuralComplete({
        kural_id: kuralId,
        language: localStorage.getItem('appLanguage') || 'en',
        time_to_complete_sec: null,
      });
      setShowMeaningPopup({ isVisible: true, kuralObj });
    }
  };

  const commitBlankIfFilled = async (kuralId, index, rawValue) => {
    if (!typeKuralMode) return;
    const kuralObj = kuralData.find((k) => k.id === kuralId);
    if (!kuralObj || !kuralObj.blanks.includes(index)) return;

    const alreadyFilled = getWordFromProgress(kuralId, index);
    if (alreadyFilled) return;

    const typedValue = rawValue.trim();
    if (!typedValue) return;

    trackKuralWordEnter({ kural_id: kuralId, word: typedValue, position: index, method: 'type' });

    const dedupeId = `${kuralId}-${index}-${typedValue}`;
    const now = Date.now();
    if (
      lastCommitDedupeRef.current.id === dedupeId &&
      now - lastCommitDedupeRef.current.t < 400
    ) {
      return;
    }
    lastCommitDedupeRef.current = { id: dedupeId, t: now };

    const cellKey = `${kuralId}-${index}`;

    if (submittingCellRef.current === cellKey) return;
    submittingCellRef.current = cellKey;

    try {
      if (!isBlankWordCorrect(kuralObj, index, typedValue)) {
        const attemptKey = `${kuralId}-${index}`;
        wrongAttemptCountsRef.current[attemptKey] = (wrongAttemptCountsRef.current[attemptKey] || 0) + 1;
        trackKuralAnswerWrong({
          kural_id: kuralId,
          word_attempted: typedValue,
          correct_word: kuralObj.tamil[index],
          attempt_count: wrongAttemptCountsRef.current[attemptKey],
        });
        await trackInteraction("KURAL_WRONG_TYPED", {
          kuralId,
          position: index,
          attemptedWord: typedValue,
          attempt_count: wrongAttemptCountsRef.current[attemptKey],
        });
        setOopsMessage("");
        setShowOopsPopUp(true);
        return;
      }

      const { submitResult, userData: latestUser } = await submitKuralWord(
        kuralId,
        index,
        typedValue
      );

      if (!submitResult || submitResult.message === "Invalid kural word. Not saved.") {
        setOopsMessage("");
        setShowOopsPopUp(true);
        return;
      }

      try {
        const tamilWord = kuralObj.tamil?.[index];
        if (tamilWord && typedValue.trim().normalize('NFC') !== tamilWord.trim().normalize('NFC')) {
          setInputs((prev) => ({
            ...prev,
            [kuralId]: {
              ...(prev[kuralId] || {}),
              [index]: tamilWord,
            },
          }));
        }
      } catch (e) {
        // ignore
      }

      trackKuralWordSubmit({
        kural_id: kuralId,
        word_position: index,
        is_correct: true,
      });
      trackKuralAnswerCorrect({ kural_id: kuralId, word_position: index, time_to_enter_ms: null });

      maybeCompletePopupAfterSubmit(kuralId, latestUser);
    } finally {
      submittingCellRef.current = null;
    }
  };

  const handleInputClick = (e, kuralId, index) => {
    const kuralObj = kuralData.find((k) => k.id === kuralId);
    if (!kuralObj) return;

    const alreadyFilled = !!getWordFromProgress(kuralId, index);

    const focusBlank = (blankIndex) => {
      requestAnimationFrame(() => {
        const targetInput = inputRefs.current[`${kuralId}-${blankIndex}`];
        if (targetInput) {
          targetInput.removeAttribute('readonly');
          targetInput.focus({ preventScroll: true });
          targetInput.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    };

    if (alreadyFilled) {
      const sortedBlanks = [...kuralObj.blanks].sort((a, b) => a - b);
      const nextEmpty = sortedBlanks.find(
        (blankIdx) => !getWordFromProgress(kuralId, blankIdx)
      );
      if (nextEmpty !== undefined) focusBlank(nextEmpty);
    } else {
      focusBlank(index);
    }
  };

  const handleChange = (kuralId, index, value) => {
    const currentKuralData = kuralData.find((k) => k.id === kuralId);
    if (!currentKuralData) return;

    const tamilWord = currentKuralData.tamil[index];
    const englishWordObj = currentKuralData.english[index];

    const englishWord =
      typeof englishWordObj === "string"
        ? englishWordObj
        : englishWordObj.word;

    const cleanValue = value.replace(/[^a-zA-Z\u0B80-\u0BFF\s]/g, "");

    const variantLengths =
      typeof englishWordObj === "string"
        ? []
        : (englishWordObj.variants || []).map((v) => v.length);

    const maxLength = Math.max(
      tamilWord.length,
      englishWord.length,
      ...variantLengths
    );

    const finalValue = cleanValue.slice(0, maxLength);

    setInputs((prev) => ({
      ...prev,
      [kuralId]: {
        ...(prev[kuralId] || {}),
        [index]: finalValue,
      },
    }));
  };

  const colors = ["orange", "green", "blue"];
  const color = colors[(kural.id - 1) % colors.length];

  const totalUnlocked = [...activeKurals, ...completedKurals].length;
  const kuralScreenClass =
    totalUnlocked === 3 ? 'second' :
      totalUnlocked === 2 ? 'second' :
        'first';

  const maxKuralSize2 = (kuralId, i) => {
    const val = (getWordFromProgress(kuralId, i) || inputs[kuralId]?.[i] || "")?.toString() || "";
    const currentLength = val.length || 0;
    const baseSize = Math.max((currentLength + 1), 6);
    return baseSize;
  }

  const maxKuralSize = (k, i) => {
    return Math.max(
      k.tamil[i].length,
      typeof k.english[i] === "string"
        ? k.english[i].length
        : k.english[i].word.length,
      ...(typeof k.english[i] === "string"
        ? []
        : (k.english[i].variants || []).map((v) => v.length))
    );
  }

  const getDefaultCompletedKuralId = () => {
    if (completedKurals.length === 0) return null;
    return [...completedKurals].sort((a, b) => a - b)[0];
  };

  const renderButtons = () => {
    const showScan = completedKurals.includes(showCardClickPopup) ? false : true;
    const showTypeKural = completedKurals.includes(showCardClickPopup) ? false : true;
    const hasTypableBlanks = completedKurals.includes(showCardClickPopup) ? false : true;
    const showRecordVideo = completedKurals.includes(showCardClickPopup) ? true : false;

    const navigateToShareVideo = (kuralId) => {
      const id = Number(kuralId);
      localStorage.setItem("selectedKuralNumber", id.toString());
      window.location.href = "/share-video";
    };

    return (
      <>
        {showScan && (
          <button
            key="scan"
            className="kural-pop-btn"
            onClick={goToBiscuitScan}
          >
            <span style={{ fontSize: language === "ta" ? "10px" : "15px" }}>
              {t("dashboard.scanBiscuit")}
            </span>
          </button>
        )}

        {showTypeKural && (
          <button
            key="type"
            type="button"
            className="kural-pop-btn"
            onClick={() => {
              const wordsAlreadyScanned = (userData?.kuralProgress || []).filter((p) => p.kuralNumber === showCardClickPopup).length;
              trackTypeKuralTap({ kural_id: showCardClickPopup, words_already_scanned: wordsAlreadyScanned });
              const targetKuralId = showCardClickPopup;
              setShowCardClickPopup(0);
              if (!hasTypableBlanks) return;
              setTypeKuralMode(true);
              requestAnimationFrame(() => {
                const card = document.getElementById(`kural-card-${targetKuralId}`);
                const firstEmpty = card?.querySelector('input.line:not([readonly])');
                if (firstEmpty) {
                  firstEmpty.removeAttribute('readonly');
                  firstEmpty.focus({ preventScroll: true });
                  firstEmpty.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              });
            }}
          >
            <span style={{
              fontSize: language === "ta" ? "10px" : "15px",
              lineHeight: "1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "pre-line",
            }}>
              {t("dashboard.TypeKural")}
            </span>
          </button>
        )}

        {showRecordVideo && (
          <button
            className="kural-pop-btn"
            onClick={() => {
              navigateToShareVideo(showCardClickPopup);
            }}
          >
            <span style={{ fontSize: language === "ta" ? "10px" : "15px" }}>
              {t("dashboard.recordVideo")}
            </span>
          </button>
        )}
      </>
    );
  };

  // ══════════════════════════════════════════════════════════
  //  NATIVE CAMERA BISCUIT SCAN (opens phone's own camera app)
  // ══════════════════════════════════════════════════════════

  // Trigger the hidden file input — this opens the phone's native camera app
  const openNativeCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // allow re-selecting the same photo twice
      fileInputRef.current.click();
    }
  };

  const closeScanPopup = () => {
    setScanPopupType(null);
    setScanPopupData(null);
  };

  // ── "Go Back" from a scan-failure popup (error / try again / oops) ──
  // If the user hasn't found/unlocked any kural yet, closing the popup
  // should bring back the "How to Play" popup to guide them.
  // If they already have at least one kural (active or completed),
  // Go Back should simply close the popup and do nothing else.
  const handleScanPopupGoBack = () => {
    const hasAnyKuralFound = totalUnlocked > 0;
    closeScanPopup();
    if (!hasAnyKuralFound) {
      setShowPopup(true);
    }
  };

  // ── Render a scan-failure reason string, bolding the final line ──
  // If the text contains "\n" (e.g. "No Biscuit Detected.\nScan again."),
  // everything before the last line break renders normally, and the
  // last line renders bold on its own line. Plain single-line reasons
  // (like the "already scanned" message) render unchanged.
  const renderReasonText = (reasonText) => {
    const lines = (reasonText || "").split("\n");
    if (lines.length > 1) {
      return (
        <>
          {lines.slice(0, -1).map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              <br />
            </React.Fragment>
          ))}
          <span className="fw-700">{lines[lines.length - 1]}</span>
        </>
      );
    }
    return reasonText;
  };

  // ── Map backend biscuit-scan failure "reason" text to a translated,
  //     user-facing message plus a stable tracking key for analytics.
  //     Backend examples handled here:
  //       - "Other Biscuit Detected"      -> Non-Kural biscuit message
  //       - "More Than One Biscuit ..."   -> Multiple biscuits message
  //       - "No Biscuit Detected"         -> No biscuit message
  //       - anything containing "already" -> Already scanned message
  const resolveScanFailureMessage = (reasonText) => {
    const r = (reasonText || "").toLowerCase();

    if (r.includes("other biscuit")) {
      return { trackingReason: "non_kural_biscuit", message: t("scan.nonKuralBiscuit") };
    }
    if (r.includes("more than one") || r.includes("multiple")) {
      return { trackingReason: "multiple_biscuits", message: t("scan.multipleBiscuits") };
    }
    if (r.includes("no biscuit")) {
      return { trackingReason: "no_biscuit_detected", message: t("scan.noBiscuitDetected") };
    }
    if (r.includes("already")) {
      return { trackingReason: "already_scanned", message: t("scan.alreadyScanned") };
    }

    // Fallback: unknown reason from backend — show it as-is if present,
    // otherwise fall back to the generic "no biscuit" message.
    return {
      trackingReason: reasonText || "no_biscuit",
      message: reasonText || t("scan.noBiscuit"),
    };
  };

  const handleFileCaptured = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setScanPopupData(null);
    setScanPopupType("loading");
    scanStartTimeRef.current = Date.now();
    trackBiscuitScanStart();

    try {
      const mobile = localStorage.getItem("userMobile") || "";
      const formData = new FormData();
      formData.append("image", file, file.name || "biscuit.jpg");
      formData.append("mobile", mobile);

      const response = await api.post("/detect-biscuit-text", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      const { success, reason, status, kural_matches, popup } = response.data;

      // ── Backend returned failure (2xx body) ──────────────
      if (!success) {
        if (status === "TRY_AGAIN") {
          biscuitScanFailCountRef.current += 1;
          trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: 'unreadable', attempt_count: biscuitScanFailCountRef.current });
          setScanPopupData({ reason: reason || t("scan.textUnreadable") });
          setScanPopupType("try_again");
          return;
        }

        // status === "INVALID" (or any other non-TRY_AGAIN failure status),
        // e.g. { success: false, status: "INVALID", reason: "Other Biscuit Detected" }
        biscuitScanFailCountRef.current += 1;
        const { trackingReason, message } = resolveScanFailureMessage(reason);
        trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: trackingReason, attempt_count: biscuitScanFailCountRef.current });
        setScanPopupData({ reason: message });
        setScanPopupType("error");
        return;
      }

      // ── No matches array at all ───────────────────────────
      if (!kural_matches || kural_matches.length === 0) {
        biscuitScanFailCountRef.current += 1;
        trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: 'no_match', attempt_count: biscuitScanFailCountRef.current });
        setScanPopupData({ reason: t("scan.noBiscuit") });
        setScanPopupType("error");
        return;
      }

      // ── Has matches but none are confident ────────────────
      const confidentMatches = kural_matches.filter((m) => m.confident);
      if (confidentMatches.length === 0) {
        biscuitScanFailCountRef.current += 1;
        trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: 'low_confidence', attempt_count: biscuitScanFailCountRef.current });
        setScanPopupData({ reason: t("scan.textUnreadable") });
        setScanPopupType("try_again");
        return;
      }

      // ── Success ───────────────────────────────────────────
      const scanDuration = scanStartTimeRef.current ? Date.now() - scanStartTimeRef.current : null;
      const topKural = confidentMatches[0];
      trackBiscuitScanSuccess({
        kural_id: topKural?.kural_id || topKural?.kuralNumber || '',
        scan_duration_ms: scanDuration,
        cashback_amount: topKural?.cashback || 0,
      });
      trackKuralUnlocked({
        kural_id: topKural?.kural_id || topKural?.kuralNumber || '',
        kural_chapter: topKural?.chapter || '',
        cashback_amount: topKural?.cashback || 0,
      });

      const popupKind =
        popup === "success-without-cashback"
          ? "success-without-cashback"
          : "success";

      setScanPopupData({ kural_matches });
      setScanPopupType(popupKind);

      if (response.data.showCompletionPopup && response.data.completedKuralNumber) {
        localStorage.setItem(
          "pendingKuralComplete",
          String(response.data.completedKuralNumber)
        );
      }

      // ── Auto-call cashback API silently in background ─────
      if (popupKind === "success") {
        try {
          await api.post("user/enable-cashback", {}, { withCredentials: true });
        } catch (cashbackErr) {
          console.error("Auto cashback API error:", cashbackErr);
        }
      }

      // ── Refresh dashboard data behind the popup ────────────
      try {
        const refreshed = await api.get("/user/userData", { withCredentials: true });
        setUserData(refreshed.data.data);
      } catch (e) {
        // ignore
      }

    } catch (err) {
      console.error("API error:", err);
      const resData = err?.response?.data;
      const serverStatus = resData?.status;
      const httpStatus = err?.response?.status;
      const serverReason =
        resData?.reason || resData?.error || err?.message;

      if (httpStatus === 403 && serverStatus === "INSUFFICIENT_CREDITS") {
        setScanPopupData({
          reason:
            serverReason ||
            "You are out of scans! Scan a new pack to continue scanning.",
        });
        setScanPopupType("oops_simple");
        return;
      }

      if (httpStatus === 409 && serverStatus === "ALREADY_SCANNED") {
        setScanPopupData({
          reason: t("scan.alreadyScanned"),
        });
        setScanPopupType("oops_simple");
        return;
      }

      if (serverReason === "Text is unreadable") {
        setScanPopupData({ reason: t("scan.textUnreadable") });
        setScanPopupType("try_again");
        return;
      }

      setScanPopupData({ reason: serverReason || t("scan.genericError") });
      setScanPopupType("error");
    }
  };

  const renderScanPopupContent = () => {
    switch (scanPopupType) {

      // ── Loading ────────────────────────────────────────────
      case "loading":
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
            <Lottie animationData={loading} loop={true} />
          </div>
        );

      // ── Success (with cashback) ────────────────────────────
      case "success":
        return (
          <div className="scan-popup">
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="scan-success-level-1-box">
              <div className="scan-success-level-1-box-scroll">
                <div className="scan-success-level-1-box-inner">
                  <div className="star-rotate">
                    <img src="assets/images/cashback-star-element.webp" className="orange-star-bg img-fit" />
                  </div>
                  <div className="scan-success-level-1-box-text scan-instruction-box">
                    <div className="cashback-wrapper">
                      <img src={language === "ta" ? "assets/images/voucher-text-tamil.webp" : "assets/images/voucher-text.webp"} className="cashback-icon voucher-icon" />
                    </div>
                    <p style={{ fontSize: language === "ta" ? "10px" : "16px", padding: language === "ta" ? "0 14px" : "0" }}>
                      {language === "en" && <b>{t("claimCashback.congratulations")}<br /></b>}
                      {t("claimCashback.step1Text")}
                    </p>
                  </div>
                </div>
                <div className="scan-success-level-1-box-footer">
                  <button
                    className="btn-next claim-reward-btn-level-1"
                    type="button"
                    onClick={() => {
                      trackCashbackClaimTap({ reward_amount: scanPopupData?.kural_matches?.[0]?.cashback ?? null });
                      closeScanPopup();
                      redirectToRoute(navigate, getPostBiscuitScanTarget());
                    }}
                  >
                    <span>{t("claimCashback.step1Button")}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      // ── Oops (single message + Go Back) ───────────────────
      case "oops_simple":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="biscuit-error-box">
              <div className="error-bis-scroll">
                <div className="bis-error-scroll-inner">
                  <img src="assets/images/language-popup-bg.webp" className="img-fit" alt="Language Background" />
                  <div className="bis-error-text">
                    <img
                      src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                      className="oops-icon"
                      alt="Oops Icon"
                    />
                    <p style={{
                      fontSize: language === "ta" ? "10px" : "13px",
                      padding: language === "ta" ? "0 14px" : "0",
                      maxWidth: "220px",
                      margin: "0 auto",
                      textAlign: "center",
                      lineHeight: "1.35",
                    }}>
                      {renderReasonText(scanPopupData?.reason || t("scan.genericError"))}
                    </p>
                  </div>
                </div>
                <div className="bis-error-scroll-footer">
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8px 0px" }}
                    onClick={handleScanPopupGoBack}
                  >
                    <span>{t("popup.goBack")}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      // ── Try Again ──────────────────────────────────────────
      case "try_again":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="biscuit-error-box">
              <div className="error-bis-scroll">
                <div className="bis-error-scroll-inner">
                  <img src="assets/images/language-popup-bg.webp" className="img-fit" alt="Language Background" />
                  <div className="bis-error-text">
                    <img
                      src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                      className="oops-icon"
                      alt="Oops Icon"
                    />
                    <p style={{ fontSize: language === "ta" ? "10px" : "13px", padding: language === "ta" ? "0 14px" : "0" }}>
                      {t("scan.scanFailed")}{" "}
                      <span className="fw-700">{t("scan.tryAgain")}</span>
                    </p>
                  </div>
                </div>
                <div className="bis-error-scroll-footer">
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8px 0px" }}
                    onClick={handleScanPopupGoBack}
                  >
                    <span>{t("common.goBack")}</span>
                  </button>
                  {/* <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8px 0px" }}
                    onClick={() => {
                      closeScanPopup();
                      openNativeCamera();
                    }}
                  >
                    <span>{t("common.scan")}</span>
                  </button> */}
                </div>
              </div>
            </div>
          </>
        );

      // ── Error ──────────────────────────────────────────────
      case "error":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="biscuit-error-box">
              <div className="error-bis-scroll">
                <div className="bis-error-scroll-inner">
                  <img src="assets/images/language-popup-bg.webp" className="img-fit" alt="Language Background" />
                  <div className="bis-error-text">
                    <img
                      src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                      className="oops-icon"
                      alt="Oops Icon"
                    />
                    <p style={{ fontSize: language === "ta" ? "10px" : "13px", padding: language === "ta" ? "0 14px" : "0" }}>
                      {renderReasonText(scanPopupData?.reason || t("scan.genericError"))}
                    </p>
                  </div>
                </div>
                <div className="bis-error-scroll-footer">
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    onClick={handleScanPopupGoBack}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8px 0px" }}
                  >
                    <span>{t("common.goBack")}</span>
                  </button>
                  {/* <button
                    className="btn-next error-bis-btn"
                    type="button"
                    onClick={() => {
                      closeScanPopup();
                      openNativeCamera();
                    }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8px 0px" }}
                  >
                    <span>{t("common.scan")}</span>
                  </button> */}
                </div>
              </div>
            </div>
          </>
        );

      // ── Success Without Cashback ───────────────────────────
      case "success-without-cashback":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="scan-success-level-1-box">
              <div className="scan-success-level-1-box-scroll">
                <div className="scan-success-level-1-box-inner">
                  <div className="star-rotate">
                    <img src="assets/images/Star-2.webp" className="orange-star-bg img-fit" alt="Star" />
                  </div>
                  <div className="scan-success-level-1-box-text">
                    <div className="cashback-wrapper">
                      <img
                        src={language === "ta" ? "assets/images/machineCodeScan-tamil.webp" : "assets/images/your-scan-was-SUCCESSFul.webp"}
                        className="cashback-icon"
                        alt="Scan Success"
                      />
                    </div>
                  </div>
                </div>
                <div className="scan-success-level-1-box-footer">
                  <button className="btn-next claim-reward-btn-level-1" type="button" onClick={closeScanPopup}>
                    <span>{t("common.continue")}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <link rel="shortcut icon" href="assets/images/favicon.ico" type="image/x-icon" />
      <link rel="stylesheet" href="https://use.typekit.net/hiu4ulc.css" />
      <link rel="stylesheet" href="assets/css/styles.css?ver=2.0" />
      <title>Home | Britannia</title>

      {/* Hidden native-camera file input. `capture="environment"` opens the
          phone's own camera app (not an in-browser video feed). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileCaptured}
      />

      <div className="page-wrapper">
        <div className="main-wrapper yellow-bg">

          {/* ══ DESKTOP ══ */}
          <div className="d-frame-outer desktop-only">
            <img src="assets/images/desktop-star.webp" className="d-frame-star-bg" />
            <span className="d-frame-corner tl" />
            <span className="d-frame-corner tr" />
            <span className="d-frame-corner bl" />
            <span className="d-frame-corner br" />
            <div className="d-frame-border-outer">
              <div className="d-frame-border">
                <div className="d-frame-border-inner">
                  <div className="d-frame-canvas">
                    <div className="d-frame-content">
                      <div className="desktop-center">
                        <div className="desktop-content-box">
                          <h3>{t("common.desktopInterest")}</h3>
                          <img src="assets/images/desktop-logo-milk-bikis.webp" className="desktop-logo" alt="Milk Bikis Logo" />
                          <div className="desktop-qr-box">
                            <span className="qr-corner qr-corner-tl" />
                            <span className="qr-corner qr-corner-tr" />
                            <span className="qr-corner qr-corner-bl" />
                            <span className="qr-corner qr-corner-br" />
                            <div className="qr-left">
                              <p className="qr-text">{t("common.desktopQr")}</p>
                            </div>
                            <div className="qr-right">
                              <img src="assets/images/desktop-qr.svg" alt="QR Code" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ══ MOBILE ══ */}
          <div className="frame-outer mobile-only">
            <span className="frame-corner tl" />
            <span className="frame-corner tr" />
            <span className="frame-corner bl" />
            <span className="frame-corner br" />
            <div className="frame-border-outer">
              <div className="frame-border">
                <div
                  className="frame-border-inner"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <div
                    className="frame-canvas"
                    style={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      WebkitOverflowScrolling: "touch",
                      minHeight: 0,
                    }}
                  >
                    <div className="frame-content">

                      <div className={`kural-screen kural-${kuralScreenClass}`}>

                        <div className="kural-top">
                          <div className="kural-head-container">
                            <div className="logo-head">
                              <div className="camera-sc-logo-wrap">
                                <img src="assets/images/animate-britania-logo.webp" alt="Britannia" className="camera-sc-logo camera-sc-logo-1" />
                                <img src="assets/images/animate-milk-bikkis-logo.webp" alt="Milk Bikis" className="camera-sc-logo camera-sc-logo-2" />
                              </div>
                              <img
                                src="assets/images/sign-up-head-text.webp"
                                className="top-logo-text"
                                alt="Top Logo"
                                onClick={() => {
                                  trackHomeTap({ from_screen: 'dashboard', kural_in_progress: activeKurals.length > 0 });
                                  window.location.href = "/home";
                                }}
                              />
                              <div className="menu-bars" onClick={() => navigate("/menu")}>
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            </div>

                            <div className="menu-head-box">
                              <div className="menu-bar-box">
                                <div className="user-profile">
                                  <img src="assets/images/user-icon.webp" className="img-fit user-icon" alt="User Icon" />
                                  <p><a href="#">{t("dashboard.greeting", { name: userData?.name || "Unknown" })}</a></p>
                                </div>
                                <LanguageToggle />
                              </div>
                              <div className="kural-head-box">
                                <img src="assets/images/kural-heading-bg.webp" alt="Kural Heading Background" className="kural-head-bg img-fit" />
                                <div className="text-kural-heading">
                                  <img
                                    src={language === "ta" ? "assets/images/kural-head-text-tamil.webp" : "assets/images/kural-head-text.webp"}
                                    alt="kural heading text"
                                    className="img-fit kural-head-text"
                                  />
                                  <p className={`scan-instruction ${language === "ta" ? "scan-instruction-ta" : ""
                                    }`}>
                                    {t("dashboard.scanInstruction")}
                                  </p>
                                </div>
                                <div className="biscuit-element-1">
                                  <img src="assets/images/biscuit-element-1.webp" className="img-fit" alt="Biscuit" />
                                </div>
                                <div className="biscuit-element-2">
                                  <img src="assets/images/biscuit-element-2.webp" className="img-fit" alt="Biscuit" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="kural-section">
                            <div style={{ marginBottom: '10px', fontWeight: '600', fontSize: '14px' }}>
                              {t("dashboard.kuralOptionsInstruction")}
                            </div>

                            {kuralData
                              .filter(k => activeKurals.includes(k.id) || completedKurals.includes(k.id))
                              .sort((a, b) => a.id - b.id)
                              .map((k) => {
                                const kuralId = k.id;
                                const isCompleted = completedKurals.includes(kuralId);
                                const cardColor = isCompleted ? "green" : "orange";

                                const starSrc =
                                  cardColor === "green"
                                    ? "assets/images/star-element-green.webp"
                                    : cardColor === "blue"
                                      ? "assets/images/star-element-blue.webp"
                                      : "assets/images/kural-element-star.webp";

                                return (
                                  <div
                                    className={`kural-wrapper ${cardColor} ${isCompleted ? "completed" : ""}`}
                                    key={kuralId}
                                    id={`kural-card-${kuralId}`}
                                    onClick={(e) => {
                                      setShowCardClickPopup(kuralId);
                                    }}
                                  >
                                    <div className={`kural-card ${cardColor}`}>
                                      <h3 className="kural-title">
                                        <span className="star"><img src={starSrc} className="img-fit" /></span>
                                        {t("dashboard.kuralTitle", { id: k.id })}
                                        <span className="star"><img src={starSrc} className="img-fit" /></span>
                                      </h3>

                                      <div className="kural-lines">
                                        <div className="line-group">
                                          {k.tamil.map((word, i) => (
                                            <React.Fragment key={i}>
                                              {k.blanks.includes(i) ? (
                                                <>
                                                  {(() => {
                                                    const currentValue = (inputs[k.id]?.[i] || getWordFromProgress(k.id, i) || "").toString();
                                                    const isCorrect = isBlankWordCorrect(k, i, currentValue);

                                                    const baseBorderColor =
                                                      cardColor === "green"
                                                        ? "#1ca74e"
                                                        : cardColor === "blue"
                                                          ? "#3456ff"
                                                          : "#ff5b1f";

                                                    return (
                                                      <>
                                                        <input
                                                          className={`line ${isCorrect ? 'correct' : ''}`}
                                                          value={currentValue}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!typeKuralMode) {
                                                              setShowCardClickPopup(k.id);
                                                            } else {
                                                              handleInputClick(e, k.id, i);
                                                            }
                                                          }}
                                                          onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleChange(k.id, i, e.target.value);
                                                          }}
                                                          onBlur={(e) => commitBlankIfFilled(k.id, i, e.target.value)}
                                                          onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                              e.preventDefault();
                                                              commitBlankIfFilled(k.id, i, e.currentTarget.value);
                                                            }
                                                          }}
                                                          maxLength={maxKuralSize(k, i)}
                                                          ref={(el) => (inputRefs.current[`${k.id}-${i}`] = el)}
                                                          inputMode="text"
                                                          autoComplete="off"
                                                          readOnly={!!getWordFromProgress(k.id, i) || !typeKuralMode}
                                                          style={{
                                                            background: "transparent",
                                                            border: "none",
                                                            outline: "none",
                                                            borderColor: isCorrect ? "#1ca74e" : baseBorderColor,
                                                            borderBottom: isCorrect ? "1px solid #1ca74e" : `1px solid ${baseBorderColor}`,
                                                            textAlign: "center",
                                                            fontSize: "12px",
                                                            width: `calc(${maxKuralSize2(k.id, i)}ch)`,
                                                          }}
                                                        />
                                                        {i === 3 && <div style={{ width: '100%' }} />}
                                                      </>
                                                    );
                                                  })()}
                                                </>
                                              ) : (
                                                <span
                                                  onClick={(e) => e.stopPropagation()}
                                                  style={{
                                                    background: "transparent",
                                                    border: "none",
                                                    outline: "none",
                                                    borderColor: "#1ca74e",
                                                    borderBottom: "1px solid #1ca74e",
                                                    textAlign: "center",
                                                    fontSize: language === "ta" ? "8px" : "12px",
                                                    width: `calc(${word.length}ch)`,
                                                  }}
                                                >{word}</span>
                                              )}
                                              {![3, 6].includes(i) && (
                                                <span className="slash">/</span>
                                              )}
                                            </React.Fragment>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <button
                                key="scan"
                                className="btn-next"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  alignSelf: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  padding: "8px 0px",
                                }}
                                onClick={goToBiscuitScan}
                              >
                                <span style={{ fontSize: language === "ta" ? "10px" : "15px" }}>
                                  {t("dashboard.scanBiscuit")}
                                </span>
                              </button>
                            </div>
                          </div>

                        </div>

                      </div>

                    </div>
                  </div>

                  <div
                    className="kural-bottom"
                    style={{ flexShrink: 0 }}
                  >
                    <div className="kural-steps">
                      <span style={{ fontSize: language === "ta" ? "6px" : "8px" }}>{t("dashboard.scanStep")}</span>
                      <img src="assets/images/right-arrow-icon.webp" alt="arrow" className="arrow-img" />
                      <span style={{ fontSize: language === "ta" ? "6px" : "8px" }}>{t("dashboard.completeStep")}</span>
                      <img src="assets/images/right-arrow-icon.webp" alt="arrow" className="arrow-img" />
                      <span style={{ fontSize: language === "ta" ? "6px" : "8px" }}>{t("dashboard.sendVideoStep")}</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
            <div className="frame-footer">
              <p className="frame-footer-terms">
                <Link to="/tnc">{t("common.terms")}</Link> {t("common.and")} <Link to="/privacy-policy">{t("common.privacy")}</Link>
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* ══ WELCOME BACK POPUP ══ */}
      {(welcomeBackPopup) && (
        <div className="scan-popup welcome-notice">
          <div className="popup-card">
            <img src="assets/images/star-element.webp" className="popup-star" alt="Sparkle Element" />
            <div className="popup-inner">
              <img
                src={language === "ta" ? "assets/images/welcome-back-notice-tamil.webp" : "assets/images/welcome-back-notice.webp"}
                alt="Congrats"
                className="img-fit wel-notice-img"
              />
              <p className="popup-text">{t("welcomeBack.message")}<b>{t("welcomeBack.messageBoldPart")}</b></p>
              <p style={{ marginBottom: '18px', marginTop: '-12px' }} className="frame-footer-terms">
                <a
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTncPopup(true);
                  }}
                >
                  {t("common.terms")}
                </a>{" "}
              </p>
            </div>
            <button className="btn-next popup-btn" type="submit" onClick={() => setWelcomeBackPopup(false)}>
              <span>{t("welcomeBack.continue")}</span>
            </button>
          </div>
        </div>
      )}

      {/* ══ CARD CLICK POPUP ══ */}
      {showCardClickPopup && (
        <div
          className="kural-click-popup"
          onClick={() => setShowCardClickPopup(0)}
        >
          <div
            className="popup-card kural-popup-card"
            onClick={(e) => e.stopPropagation()}
          >
            <img src="assets/images/star-element.webp" className="popup-star" alt="Sparkle Element" />
            <div className="popup-inner">
              <button
                className="howtp-close-btn"
                data-close-popup=".kural-click-popup"
                onClick={() => setShowCardClickPopup(0)}
              >
                <img src="assets/images/close-icon.webp" alt="Close" />
              </button>
              <div className="kural-popup-btns">
                {renderButtons()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ HOW TO PLAY POPUP ══ */}
      {showPopup && (
        <div className="scan-popup how-to-play-popup" ref={howToPlayRef}>
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="popup-card">
            <img
              src="assets/images/how-to-play-biscuit-element.webp"
              className="how-top-biscuit"
              alt="Biscuit Element"
            />
            <div className="popup-inner">
              <button
                className="howtp-close-btn"
                onClick={() => setShowPopup(false)}
              >
                <img src="assets/images/close-icon.webp" alt="close" />
              </button>
              <div className="how-to-text">
                <img
                  src={
                    language === "ta"
                      ? "assets/images/how-to-play-tamil.webp"
                      : "assets/images/how-to-play.webp"
                  }
                  className="img-fit"
                  alt="How to Play"
                />
                <div className="how-level-con">

                  {/* ── Level 1 ── */}
                  <div className="level-header">
                    <div className="level-box">{t("popup.level1")}</div>
                    <button
                      type="button"
                      className="tutorial-btn"
                      onClick={() => {
                        setSelectedLevel(1);
                        setShowTutorialVideo(true);
                      }}
                    >
                      {t("popup.tutorial")}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 3h7v7" />
                        <path d="M10 14L21 3" />
                        <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                      </svg>
                    </button>
                  </div>
                  <p style={{ fontSize: language === "ta" ? "8px" : "12px" }}>
                    {t("popup.level1Text").split("\n")[0]}
                    <br />
                    {t("popup.level1Text").split("\n")[1] || ""}
                    <br />
                    <span className="fw-700">{t("popup.level1Reward")}</span>
                  </p>

                  {/* ── Level 2 ── */}
                  <div className="level-header">
                    <div className="level-box">{t("popup.level2")}</div>
                    <button
                      type="button"
                      className="tutorial-btn"
                      onClick={() => {
                        setSelectedLevel(2);
                        setShowTutorialVideo(true);
                      }}
                    >
                      {t("popup.tutorial")}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 3h7v7" />
                        <path d="M10 14L21 3" />
                        <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                      </svg>
                    </button>
                  </div>
                  <p style={{ fontSize: language === "ta" ? "8px" : "12px" }}>
                    {t("popup.level2Text").split("\n")[0]}
                    <br />
                    {t("popup.level2Text").split("\n")[1] || ""}
                  </p>
                  <p style={{ fontSize: language === "ta" ? "8px" : "12px" }}>
                    {t("popup.level2Video").split("\n")[0]}
                    <br />
                    {t("popup.level2Video").split("\n")[1] || ""}
                    <span className="fw-700">{t("popup.level2Win")}</span>
                  </p>

                </div>
              </div>
            </div>
            <button
              className="btn-next popup-btn"
              type="submit"
              onClick={goToBiscuitScan}
            >
              <span style={{ fontSize: language === "ta" ? "15px" : "18px" }}>
                {t("popup.continue")}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ══ TUTORIAL VIDEO POPUP (Dynamic — Level 1 or Level 2) ══ */}
      {showTutorialVideo && (
        <div
          className="scan-popup how-to-play-popup"
          onClick={() => setShowTutorialVideo(false)}
        >
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="popup-card" onClick={(e) => e.stopPropagation()}>
            <img
              src="assets/images/how-to-play-biscuit-element.webp"
              className="how-top-biscuit"
              alt="Biscuit Element"
            />
            <div className="popup-inner">
              <button
                className="howtp-close-btn"
                onClick={() => setShowTutorialVideo(false)}
              >
                <img src="assets/images/close-icon.webp" alt="close" />
              </button>

              <div
                style={{
                  width: "222px",
                  height: "390px",
                  border: "4px solid #FFD700",
                  overflow: "hidden",
                  boxShadow: "0 0 20px rgba(255, 215, 0, 0.5)",
                  backgroundColor: "#000",
                }}
              >
                <video
                  key={selectedLevel}
                  src={tutorialVideoUrls[selectedLevel]}
                  autoPlay
                  loop
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </div>
            </div>

            <button
              className="btn-next popup-btn"
              type="button"
              onClick={() => {
                setShowTutorialVideo(false);
                goToBiscuitScan();
              }}
            >
              <span style={{ fontSize: language === "ta" ? "15px" : "18px" }}>
                {t("popup.scanBiscuit")}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ══ MEANING POPUP ══ */}
      {showMeaningPopup.isVisible && (
        <div className="scan-popup">
          <div className="popup-card">
            <img src="assets/images/star-element.webp" className="popup-star" alt="Sparkle Element" />
            <button
              className="howtp-close-btn"
              onClick={() => setShowMeaningPopup({ isVisible: false, kuralObj: null })}
            >
              <img src="assets/images/close-icon.webp" alt="Close" />
            </button>
            <div className="popup-inner">
              <img src={language === "ta" ? "assets/images/meaning-tamil.webp" : "assets/images/meaning.webp"} alt="Congrats" className="img-fit cong-img" />
              <p className="popup-text">{showMeaningPopup.kuralObj?.tamil.join(" ")}</p>
              <p className="cong-subtext">{showMeaningPopup.kuralObj?.englishMeaning}</p>
            </div>
            <button
              className="btn-next popup-btn"
              onClick={() => {
                trackVideoPromptShown({ kural_id: showMeaningPopup.kuralObj?.id });
                setShowMeaningPopup({ isVisible: false, kuralObj: null });
                setShowCompletePopUp(true);
              }}
              type="submit"
            >
              <span>{t("common.continue")}</span>
            </button>
          </div>
        </div>
      )}

      {/* ══ COMPLETE POPUP ══ */}
      {showCompletePopUp && (
        <div className="scan-popup" ref={completePopupRef}>
          <div className="popup-card">
            <img src="assets/images/star-element.webp" className="popup-star" alt="Sparkle" />
            <div className="popup-inner">
              <img
                src={language === "ta" ? "assets/images/congrats-tamil.webp" : "assets/images/congrats.webp"}
                alt="Congrats"
                className="img-fit cong-img"
              />
              <p className="popup-text">
                {t("popup.completedKural").split("\n")[0]}<br />{t("popup.completedKural").split("\n")[1]}
              </p>
              <p className="cong-subtext">
                {t("popup.teachChild").split("\n")[0]}<br />
                {t("popup.teachChild").split("\n")[1]}<br />
                {t("popup.teachChild").split("\n")[2]}
              </p>
              <p className='cong-subtext' style={{ marginBottom: '22px' }}>
                {t("popup.entrySuccessText").split("\n")[1]}<span className="fw-700">{t("popup.cycle")}</span> {t("common.and")}
                <br />
                <span className="fw-700">{t("popup.trip")}</span>
              </p>
              <p style={{ marginBottom: '18px', marginTop: '-12px' }} className="frame-footer-terms">
                <a
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTncPopup(true);
                  }}
                >
                  {t("common.terms")}
                </a>{" "}
              </p>
            </div>
            <button
              className="btn-next popup-btn"
              onClick={() => {
                const defaultKuralId = getDefaultCompletedKuralId();
                if (defaultKuralId) {
                  localStorage.setItem("selectedKuralNumber", defaultKuralId.toString());
                  window.location.href = "/share-video";
                } else {
                  window.location.href = "/share-video";
                }
              }}
              type="submit"
            >
              <span>{t("common.continue")}</span>
            </button>
          </div>
        </div>
      )}

      {/* ══ OOPS POPUP (kural typing) ══ */}
      {showOopsPopUp && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="biscuit-error-box">
            <div className="error-bis-scroll">
              <div className="bis-error-scroll-inner">
                <img src="assets/images/language-popup-bg.webp" className="img-fit" alt="Language Background" />
                <div className="bis-error-text">
                  <img
                    src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                    className="oops-icon"
                    alt="Oops Icon"
                  />
                  <p style={{
                    maxWidth: "180px",
                    margin: "0 auto",
                    textAlign: "center",
                    lineHeight: "1.3"
                  }}>
                    {oopsMessage
                      ? oopsMessage
                      : (
                        <>
                          {t("popup.oopsText").split("\n")[0]}
                          <br />
                          <span style={{ fontWeight: "bold" }}>
                            {t("popup.oopsText").split("\n")[1]}
                          </span>
                        </>
                      )}
                  </p>
                </div>
              </div>
              <div className="bis-error-scroll-footer">
                <button
                  className="btn-next error-bis-btn"
                  onClick={() => {
                    setShowOopsPopUp(false);
                    setOopsMessage("");
                    lastCommitDedupeRef.current = { id: "", t: 0 };
                  }}
                  type="submit"
                >
                  <span>{t("popup.goBack")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ VIDEO KURAL SELECT POPUP ══ */}
      {(showVideoPopup) && (
        <div className="scan-popup select-kural-popup" id="kuralPopup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="popup-card">
            <img
              src="assets/images/star-element.webp"
              className="popup-star"
              alt="Sparkle Element"
            />
            <button
              className="howtp-close-btn"
              onClick={() => closePopup(howToPlayRef, setShowVideoPopup)}
            >
              <img src="assets/images/close-icon.webp" alt="Close" />
            </button>
            <div className="popup-inner">
              <p className="popup-text">
                {t("popup.selectKuralVideo").split("\n")[0]}<br />{t("popup.selectKuralVideo").split("\n")[1]}
              </p>
              <div className="kural-select-ui">
                {completedKurals.map((kuralId) => (
                  <label key={kuralId} className="kural-select-ui__option">
                    <input
                      type="radio"
                      name="video-kural"
                      value={kuralId}
                      className="kural-select-ui__input"
                      onChange={() => setSelectedVideoKural(Number(kuralId))}
                    />
                    <span className="kural-select-ui__box" />
                    <span className="kural-select-ui__text">Kural {kuralId}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              className="btn-next popup-btn"
              type="button"
              onClick={() => {
                if (!selectedVideoKural) return;
                const kuralId = Number(selectedVideoKural);
                localStorage.setItem("selectedKuralNumber", kuralId.toString());
                setShowVideoPopup(false);
                window.location.href = "/share-video";
              }}
            >
              <span>{t("popup.continue")}</span>
            </button>
          </div>
        </div>
      )}

      {/* ══ RECORD VIDEO / SCAN INSTRUCTIONS POPUP — Continue opens the NATIVE camera app ══ */}
      {recordVideoPopup && (
        <div className="scan-popup">
          <div className="popup-card">
            <img
              src="assets/images/star-element.webp"
              className="popup-star"
              alt="Sparkle Element"
            />

            <div className="popup-inner">
              <p
                className="popup-text"
                style={{
                  paddingLeft: language === "en" ? "20px" : "0",
                  paddingRight: language === "en" ? "20px" : "0",
                }}
              >
                {t("scan.scanYourKural")}
              </p>

              <img
                src="assets/images/referance.webp"
                style={{ height: "180px" }}
                className="popup-image"
                alt="Scan Help"
              />
            </div>

            <button
              className="btn-next popup-btn"
              type="button"
              onClick={() => {
                setRecordVideoPopup(false);
                setShowCardClickPopup(0);
                // Close this popup, then open the phone's native camera app.
                openNativeCamera();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: language === "ta" ? "13px" : "16px",
                }}
              >
                {t("scan.scanBiscuit")}
              </span>

              <svg
                width="20"
                height="20"
                viewBox="0 0 55 43"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  display: "block",
                  flexShrink: 0,
                }}
              >
                <path
                  d="M27.3634 31.2726C23.0456 31.2726 19.5453 27.7723 19.5453 23.4544C19.5453 19.1366 23.0456 15.6362 27.3634 15.6362C31.6813 15.6362 35.1816 19.1366 35.1816 23.4544C35.1816 27.7723 31.6813 31.2726 27.3634 31.2726Z"
                  fill="black"
                />
                <path
                  d="M5.86385 7.81819H13.0712C13.4377 7.81819 13.8922 7.5812 14.2464 7.20739L17.4152 2.2062C17.4653 2.12682 17.5213 2.05129 17.5826 1.9802C18.6771 0.703637 20.1565 0 21.7445 0H32.9832C34.5713 0 36.0506 0.703637 37.1452 1.9802C37.2065 2.05129 37.2624 2.12682 37.3125 2.2062L40.4813 7.21472C40.7525 7.51035 41.1337 7.82552 41.5343 7.82552V6.84825C41.5343 6.32987 41.7403 5.83272 42.1068 5.46617C42.4734 5.09962 42.9705 4.8937 43.4889 4.8937H46.4207C46.9391 4.8937 47.4362 5.09962 47.8028 5.46617C48.1693 5.83272 48.3753 6.32987 48.3753 6.84825V7.82552H48.8639C50.4185 7.82714 51.909 8.44543 53.0083 9.54473C54.1076 10.644 54.7259 12.1345 54.7275 13.6892V37.1364C54.7259 38.691 54.1076 40.1815 53.0083 41.2808C51.909 42.3801 50.4185 42.9984 48.8639 43H5.86385C4.30921 42.9984 2.81871 42.3801 1.71941 41.2808C0.620113 40.1815 0.00182343 38.691 0.000205994 37.1364V13.6818C0.00182343 12.1272 0.620113 10.6367 1.71941 9.5374C2.81871 8.4381 4.30921 7.81981 5.86385 7.81819ZM27.3639 35.1819C29.6833 35.1819 31.9507 34.4941 33.8792 33.2055C35.8077 31.9168 37.3109 30.0853 38.1985 27.9424C39.0861 25.7995 39.3183 23.4416 38.8658 21.1667C38.4133 18.8918 37.2964 16.8022 35.6563 15.1621C34.0162 13.522 31.9266 12.4051 29.6518 11.9526C27.3769 11.5001 25.0189 11.7324 22.876 12.62C20.7332 13.5076 18.9016 15.0107 17.613 16.9392C16.3244 18.8678 15.6366 21.1351 15.6366 23.4546C15.6401 26.5637 16.8768 29.5446 19.0754 31.7431C21.2739 33.9416 24.2547 35.1783 27.3639 35.1819Z"
                  fill="black"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ══ NATIVE SCAN RESULT POPUP (loading / success / error / try again / oops) ══ */}
      {scanPopupType && (
        <div className="scan-popup">
          {renderScanPopupContent()}
        </div>
      )}

      <TnCPopup
        isOpen={showTncPopup}
        onClose={() => setShowTncPopup(false)}
        showCloseButton={true}
      />
    </>
  );
}

export default Dashboard;